import React from 'react';
import { View } from 'react-native';

import { Text } from '../../components';
import { TextInput } from '../../components/input';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import ChecklistItemControl from './ChecklistItemControl';
import { createChecklistFormViewStyles } from './ChecklistFormView.styles';
import { ITEM_LAYOUT, getItemLayout } from './checklistFormHelpers';

/**
 * ChecklistItem: one checklist question with its answer control
 * (getItemLayout: beside, under or below the label), the comment field once the
 * answer is flagged, and its validation error.
 * Props: item, value, comment, validationError, isFlagged, onAnswer(itemId, value),
 * onComment(itemId, value), onAddPhoto(itemId), onRemovePhoto(itemId, link),
 * processingMedia, isCapturingPhoto, isReadOnly (submitted: no photo add/remove)
 */
const ChecklistItem = ({
	item,
	value,
	comment,
	validationError,
	isFlagged,
	onAnswer,
	onComment,
	onAddPhoto,
	onRemovePhoto,
	processingMedia,
	isCapturingPhoto,
	isReadOnly,
}) => {
	const styles = useThemedStyles(createChecklistFormViewStyles);
	const layout = getItemLayout(item);
	const control = (
		<ChecklistItemControl
			item={item}
			value={value}
			onAnswer={onAnswer}
			onAddPhoto={onAddPhoto}
			onRemovePhoto={onRemovePhoto}
			processingMedia={processingMedia}
			isCapturingPhoto={isCapturingPhoto}
			isReadOnly={isReadOnly}
		/>
	);
	const question = (
		<View style={styles.question}>
			<Text style={styles.questionLabel}>{item.label}</Text>
			{Boolean(item.description) && <Text style={styles.description}>{item.description}</Text>}
		</View>
	);

	let content;
	if (layout === ITEM_LAYOUT.ROW) {
		content = (
			<View style={styles.rowTop}>
				{question}
				{control}
			</View>
		);
	}
	else if (layout === ITEM_LAYOUT.STACKED) {
		content = (
			<>
				{question}
				<View style={styles.stackedControl}>{control}</View>
			</>
		);
	}
	else {
		content = (
			<>
				<Text style={styles.blockLabel}>{item.label}</Text>
				{Boolean(item.description) && <Text style={styles.description}>{item.description}</Text>}
				{control}
			</>
		);
	}

	return (
		<View
			style={[layout === ITEM_LAYOUT.BLOCK ? styles.blockItem : styles.rowItem, Boolean(validationError) && styles.itemError]}
		>
			{content}
			{
				// A submitted checklist shows only a comment that was written, as text.
				isFlagged && isReadOnly && Boolean(comment) && (
					<View style={styles.readOnlyField}>
						<Text style={styles.readOnlyFieldText}>{comment}</Text>
					</View>
				)
			}
			{
				isFlagged && !isReadOnly && (
					<TextInput
						value={comment || ''}
						onChange={nextValue => onComment(item.id, nextValue)}
						placeholder="Add details for your manager"
						multiline={true}
						maxLength={500}
						accessibilityLabel={`Comment for ${item.label}`}
						style={styles.commentInput}
					/>
				)
			}
			{Boolean(validationError) && <Text style={styles.errorText}>{validationError}</Text>}
		</View>
	);
};

export default ChecklistItem;
